import { AgentDetailModel, AgentLicenseModel, FileUploadResponse, RegisterAgentModel } from '@/__generated';
import AccountantForm from '@/components/Form/AgentRegisterForm/AccountantForm';
import CompanyDetailForm from '@/components/Form/AgentRegisterForm/CompanyDetailForm';
import CreditCardForm from '@/components/Form/AgentRegisterForm/CreditCardForm';
import EmergencyContractForm from '@/components/Form/AgentRegisterForm/EmergencyContractForm';
import LoginDetailForm from '@/components/Form/AgentRegisterForm/LoginDetailForm';
import PersonInChargeForm from '@/components/Form/AgentRegisterForm/PersonInChargeForm';
import UpdateAgentProfileLicenseForm from '@/components/Form/AgentRegisterForm/UpdateAgentProfileLicenseForm';
import Spin from '@/components/Spin';
import { AppEnvSetting, UNKNOWN_ERROR_MESSAGE } from '@/config';
import { AgentCompanySize, AgentType } from '@/constants';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { useGetAllCountries } from '@/hooks/dataProvider';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import { progress } from 'framer-motion';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

export default function AgentProfile() {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const { data: countries, isLoading } = useGetAllCountries();
  const [profile, setProfile] = React.useState<AgentDetailModel>();
  const [uploadedFiles, setUploadedFiles] = React.useState<AgentLicenseModel[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    control,
    reset,
    formState: { errors },
  } = useForm<any>({});

  const history = useHistory();
  const { isAdmin } = useGetUseRole();
  if (!isAdmin) {
    history.push(AppUrls.Page401);
    return null;
  }

  const getAgentProfile = () => {
    setLoading(true);
    apiSdk.AgentProfileService.getDetails()
      .then(res => {
        setProfile(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    getAgentProfile();
  }, []);

  React.useEffect(() => {
    setUploadedFiles(profile?.licenseFiles || []);
  }, [profile]);

  React.useEffect(() => {
    if (countries && profile) {
      let { licenseFiles, countryCode, agentType, ...values } = profile;
      let selectedCountry = countries.data?.find(x => x.shortCode === countryCode);
      reset({
        ...values,
        country: {
          value: countryCode,
          label: `[${selectedCountry?.phoneCode}] ${selectedCountry?.name}`,
        },
        companySize: profile.companySize ? { value: profile.companySize, label: AgentCompanySize.__getValue(profile.companySize) } : undefined,

        agentType: {
          value: agentType,
          label: AgentType.__getValue(agentType),
        },
        haveLicense: licenseFiles?.length ? 'Yes' : 'No',
        licenseFiles: null,
      });
    }
  }, [countries, profile, reset]);

  React.useEffect(() => {
    const firstError = Object.keys(errors).reduce((field: any, a) => {
      return !!errors[field] ? field : a;
    }, null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  const uploadLicenses = async (files: FileList) => {
    let fileResponses: FileUploadResponse[] = [];
    for (var i = 0; i < files.length; i++) {
      fileResponses.push(await apiSdk.AgentProfileService.uploadLicense({ formFile: files[i] }));
    }
    return fileResponses;
  };

  const updateProfile = (data: RegisterAgentModel) => {
    apiSdk.AgentProfileService.update({ body: data })
      .then(res => {
        if (res.success) {
          getAgentProfile();
          ModalUtils.success({
            title: t('base.UpdateSuccess'),
            content: t('AGENT_UPDATE_PROFILE_SUCCESS'),
          });
        } else {
          ModalUtils.error({
            content: t(res.message || UNKNOWN_ERROR_MESSAGE),
          });
        }
      })
      .catch(() => {
        ModalUtils.error({
          content: t(UNKNOWN_ERROR_MESSAGE),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onSubmit = async (data: any) => {
    setLoading(true);
    let fileIds: string[] = uploadedFiles?.map(x => x.licenseFileId || '') || [];
    let { licenseFiles, haveLicense, country, agentType, ...values } = data;
    try {
      if (haveLicense === 'Yes' && licenseFiles?.length) {
        let currentUploadedFiles = await uploadLicenses(licenseFiles);
        if (currentUploadedFiles?.some(x => !x.success)) {
          throw new Error();
        } else {
          fileIds = fileIds.concat(currentUploadedFiles.map(x => x.fileId || ''));
        }
      }

      updateProfile({
        ...values,
        agentType: agentType?.value,
        countryCode: country?.value,
        companySize: data.companySize?.value,

        licenseFileIds: haveLicense === 'Yes' ? fileIds : [],
      });
    } catch (error) {
      setLoading(false);
      ModalUtils.error({
        title: t('base.Error'),
        content: t('AGENT_UPLOAD_LICENSE_ERROR'),
      });
    }
  };

  return (
    <div>
      <div className="row y-gap-20 justify-between items-end pb-10 lg:pb-40 md:pb-32 ">
        <div className="col-12">
          <h1 className="text-30 lh-14 fw-600">{t('agentProfilePage_Title')}</h1>
          <div className="text-15 text-light-1">{t('agentProfilePage_SubTitle')}</div>
        </div>
      </div>
      <div className="py-30 px-30 rounded-4 bg-white shadow-3 " style={{ maxWidth: 900 }}>
        <Spin loading={loading || isLoading}>
          <form className="row mb-10 x-gap-5 y-gap-10 " onSubmit={handleSubmit(onSubmit)}>
            <CompanyDetailForm control={control} errors={errors} register={register} profile={profile} />
            <PersonInChargeForm errors={errors} register={register} />
            <EmergencyContractForm errors={errors} register={register} />
            <AccountantForm errors={errors} register={register} />
            {AppEnvSetting.enableEScott ? <CreditCardForm /> : null}

            <UpdateAgentProfileLicenseForm
              errors={errors}
              register={register}
              watch={watch}
              uploadedFiles={uploadedFiles}
              onRemoveUploadedFile={id => {
                setUploadedFiles(x => x.filter(f => f.licenseFileId !== id));
              }}
            />
            {/* button */}
            <div className="col-12 layout-pt-md">
              <button type="submit" className="button py-20 -dark-1 bg-blue-1 text-white w-100">
                <div className="icon-check mr-15" />
                {t('base.Update')}
              </button>
            </div>
          </form>
        </Spin>
      </div>
    </div>
  );
}
